import { acceptHMRUpdate, defineStore } from 'pinia'

import api from '@/lib/api'
import dotNotationToObject from '@/lib/utils/dotNotationToObject'
import { transformDeliveryContact } from '@/lib/utils/transformEntries'

import AnalyticsService from '@/services/AnalyticsService'

import { getProductCrumbs } from '@/components/common/contentful/helpers/contentfulHelpers'

import app from '@/app'
import settings from '@/settings'
import { useAccountPortalStore } from '@/stores/account-portal'
import { useAppStore } from '@/stores/app'
import { useAsmStore } from '@/stores/asm'
import { useCartStore } from '@/stores/cart'
import { useCartFlowStore } from '@/stores/cart-flow'
import { useCompanyStore } from '@/stores/company'
import { useCurrentInventoryStore } from '@/stores/current-inventory'
import { useInventoryStore } from '@/stores/inventory'
import { useInventoryLogStore } from '@/stores/inventory-log'
import { useInventoryThresholdsStore } from '@/stores/inventory-thresholds'
import { useOrderTemplateStore } from '@/stores/order-template'
import { useProductStore } from '@/stores/product'
import { usePunchoutStore } from '@/stores/punchout'
import { useReserveOrdersStore } from '@/stores/reserve-orders'
import { useRolesStore } from '@/stores/roles'
import { useSearchStore } from '@/stores/search'
import {
  ApiAddress,
  DeliveryContact,
  ApiInventorySavedSearch,
  ApiCurrencyField,
  ApiRegistrationPayloadRaw,
  ApiRegistrationPayload,
  ApiRegistrationResponse,
  ApiAddressRequestPayload,
  ContentfulProduct,
  ContentfulProductSkeleton,
  ContentfulProductMeta,
  ApiUserDetails,
} from '@/types'

type RecentlyViewedProduct = {
  link: string
  title: string
  categoryTitle: string
  categoryLink: string
  image: string
}
type State = {
  lastRequest: string | null
  email: string
  accountDetails: ApiUserDetails
  shippingAddresses: ApiAddress[]
  billingAddresses: ApiAddress[]
  inventorySavedSearches: ApiInventorySavedSearch[]
  deliveryContacts: DeliveryContact[]
  supportContacts: {
    name: string
    phone: string
    email: string
  }[]
  isSupportContactsLoaded: boolean
  customerServiceContacts: {
    region: string
    phone: string
    email: string
  }[]
  recentlyViewedProducts: RecentlyViewedProduct[]
  recentlyOrderedProducts: RecentlyViewedProduct[]
  isRecentProductsLoaded: boolean
  countries: { isocode: string; name: string }[]
  regions: {
    US: { countryIso: string; isocode: string; name: string }[]
    CA: { countryIso: string; isocode: string; name: string }[]
  }
}
type Getters = {
  user: (state: State) => {
    sageX3Id: string
    uid: string
    email: string
    salutation: string
    firstName: string
    lastName: string
    initials: string
    fullName: string
    shippingDetails: ApiAddress | object
    contactInformation: ApiAddress | object
    companyOrdersShipTo: ApiUserDetails['activeShipTo'] | []
    isCompanyOrdersAvailable: boolean
    itsRole: string
    departments: ApiUserDetails['departments']
    settings: {
      dateFormat?: ApiUserDetails['dateFormat']
      timeFormat?: ApiUserDetails['timeFormat']
      currency?: ApiCurrencyField
    }
    unit: {
      uid: string
      name: string
      itsApplication: boolean
    }
    isUserProspect: boolean
    myAccountWidgets: ApiUserDetails['myAccountWidgets']
  }
  isUserProspect: (state: State) => boolean
  isUserLoggedIn: (state: State) => boolean
  isSessionActive: (state: State) => boolean
  isInventoryTrackingVisible: (state: State) => boolean
}
type Actions = {
  resetAuthData: () => void
  resetAuthWithoutLastRequest: () => void
  setAuthData: (authData: {
    email: string
    shippingAddresses: ApiAddress[]
    billingAddresses: ApiAddress[]
    deliveryContacts: DeliveryContact[]
    accountDetails: ApiUserDetails
  }) => void
  setLastRequest: (date: string) => void
  login: (data: { username: string; password: string }) => Promise<void>
  logout: () => Promise<void>
  register: (data: ApiRegistrationPayloadRaw) => Promise<ApiRegistrationResponse>
  refreshAccountDetails: (userId?: string) => Promise<void>
  getShippingAddresses: (userId?: string) => Promise<void>
  getBillingAddresses: (userId?: string) => Promise<void>
  submitAddressRequest: (addressData: ApiAddressRequestPayload) => Promise<void>
  setDeliveryContacts: (userId?: string) => Promise<void>
  updateUserData: (userId?: string) => Promise<void>
  checkSession: () => Promise<void>
  resetApiSession: () => void
  initSessionState: () => Promise<void>
  createShortUrl: (url: string) => Promise<string>
  resolveShortUrl: (hash: string) => Promise<string>
  updateSomeUserInfoFields: (data: {
    dateFormat: { name: string; code: string }
    timeFormat: { name: string; code: string }
    currency: ApiCurrencyField
  }) => Promise<void>
  setSupportContacts: () => Promise<void>
  resetStateOnLoginChange: () => Promise<void>
  addRecentlyViewedProduct: (slug: string) => Promise<void>
  getRecentProducts: () => Promise<void>
  getCountriesAndRegions: () => Promise<void>
  setIsReserveOrdersExist: () => Promise<void>
  setIsRecentProductsLoaded: (isRecentProductsLoaded: boolean) => void
}

const defaultState: State = {
  lastRequest: null,
  email: '',
  accountDetails: {} as ApiUserDetails,
  shippingAddresses: [],
  billingAddresses: [],
  inventorySavedSearches: [],
  deliveryContacts: [],
  supportContacts: [],
  isSupportContactsLoaded: false,
  customerServiceContacts: [
    {
      region: 'North America, Asia Pacific, Middle East & Africa',
      phone: '+1 516-483-1196',
      email: 'customerservice@bioivt.com ',
    },
    {
      region: 'Europe',
      phone: '+44 (0) 1444 707333',
      email: 'cseurope@bioivt.com',
    },
  ],
  recentlyViewedProducts: [],
  recentlyOrderedProducts: [],
  isRecentProductsLoaded: false,
  countries: [],
  regions: {
    US: [],
    CA: [],
  },
}

const getDefaults = () => JSON.parse(JSON.stringify(defaultState))

export const useSessionStore = defineStore<string, State, Getters, Actions>('session', {
  state: () => getDefaults(),

  getters: {
    user: (state) => {
      if (!state.accountDetails || !('uid' in state.accountDetails)) {
        return {
          sageX3Id: '',
          uid: '',
          email: '',
          salutation: '',
          firstName: '',
          lastName: '',
          initials: '',
          fullName: '',
          shippingDetails: {},
          contactInformation: {},
          companyOrdersShipTo: [],
          isCompanyOrdersAvailable: false,
          itsRole: '',
          departments: [],
          settings: {},
          unit: {
            uid: '',
            name: '',
            itsApplication: false,
          },
          isUserProspect: false,
          myAccountWidgets: [],
        }
      }
      const user = state.accountDetails
      return {
        sageX3Id: user.sageX3Id || '',
        uid: user.uid || '',
        email: user.email || '',
        salutation: user.salutation || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        initials:
          user.firstName && user.lastName
            ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`
            : '',
        fullName: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : '',
        shippingDetails: user.shippingAddress || {},
        contactInformation: user.contactAddress
          ? {
              ...user.contactAddress,
              firstName: user.contactAddress.firstName || user.firstName || '',
              lastName: user.contactAddress.lastName || user.lastName || '',
              companyName: user.contactAddress.companyName || user.unit?.name || '',
            }
          : {},
        companyOrdersShipTo: user.activeShipTo || [],
        isCompanyOrdersAvailable: !!user.showAllSelectedShipTo,
        itsRole: user.itsRole || '',
        departments: user.departments || [],
        settings: {
          dateFormat: user.dateFormat,
          timeFormat: user.timeFormat,
          currency: user.currency,
        },
        unit: {
          uid: user.unit?.uid || '',
          name: user.unit?.name || '',
          itsApplication: user.unit?.itsApplication || false,
        },
        isUserProspect: !!user.unit?.prospect,
        myAccountWidgets: user.myAccountWidgets || [],
      }
    },
    isUserProspect: (state) => !!state.accountDetails?.unit?.prospect,
    isUserLoggedIn: (state) => !!state.accountDetails?.uid,
    isSessionActive: (state) => {
      const punchoutStore = usePunchoutStore()
      const isLastRequestInAllowedRange =
        state.lastRequest &&
        Math.ceil((Number(new Date()) - Number(new Date(state.lastRequest))) / 1000) <=
          settings.services.api.sessionTtl
      return isLastRequestInAllowedRange || punchoutStore.isPunchoutSession
    },
    isInventoryTrackingVisible() {
      const rolesStore = useRolesStore()
      return (
        rolesStore.isUserSuperAdmin ||
        (!!this.user.unit?.itsApplication && rolesStore.hasAtLeastOneRole)
      )
    },
  },

  actions: {
    resetAuthData() {
      this.$reset()
    },
    resetAuthWithoutLastRequest() {
      const lastRequest = this.lastRequest
      Object.assign(this, getDefaults(), { lastRequest })
    },

    setAuthData(authData) {
      Object.assign(this, authData)
    },

    setLastRequest(date: string) {
      this.lastRequest = date
    },

    async login(data: { username: string; password: string }) {
      await api.auth.login(data.username, data.password)
      await this.resetStateOnLoginChange()
      await this.updateUserData(data.username)
      const cartStore = useCartStore()
      await cartStore.getCart(true)
      if (cartStore.productsQty) {
        const cartFlowStore = useCartFlowStore()
        await cartFlowStore.setStep('view')
      }
      const searchStore = useSearchStore()
      await searchStore.setCompanyCatalogAvailability()
      await searchStore.setStandardCatalogAvailability()
    },

    async logout() {
      const asmStore = useAsmStore()
      if (asmStore.isAsmSession) {
        await asmStore.stopEmulation()
      } else {
        try {
          const appStore = useAppStore()
          appStore.toggleLoader(true)
          await api.auth.logout()
        } finally {
          this.$reset()
          await this.resetStateOnLoginChange()
          AnalyticsService.sendUserLogoutAnalytics()
          const appStore = useAppStore()
          appStore.toggleLoader(false)
        }
      }
    },

    async register(registrationData) {
      const payload = dotNotationToObject<ApiRegistrationPayloadRaw, ApiRegistrationPayload>(
        registrationData,
      )
      const { data } = await api.auth.register(payload)
      return data
    },

    async refreshAccountDetails(userId) {
      const accountDetails = await api.user.accountDetails(userId)
      this.setAuthData({
        email: accountDetails.uid,
        shippingAddresses: [...this.shippingAddresses],
        billingAddresses: [...this.billingAddresses],
        deliveryContacts: [...this.deliveryContacts],
        accountDetails,
      })
      const { itsSuperAdmin, itsRole, unit } = accountDetails
      const rolesStore = useRolesStore()
      await rolesStore.setRoles({
        itsSuperAdmin: !!itsSuperAdmin,
        itsRole: itsRole || '',
        allowedSubscriptionTypes: unit?.allowedSubscriptionTypes || [],
      })
      const accountPortalStore = useAccountPortalStore()
      await accountPortalStore.initWidgets()
    },

    async getShippingAddresses(userId) {
      const response = await api.user.getShippingAddresses(userId)
      const addresses = response.data?.length ? response.data.filter((el) => el) : []
      this.shippingAddresses = addresses
    },

    async getBillingAddresses(userId) {
      const response = await api.user.getBillingAddresses(userId)
      const addresses = response.data?.length ? response.data.filter((el) => el) : []
      this.billingAddresses = addresses
    },

    async submitAddressRequest(addressData) {
      await api.user.submitAddressRequest(addressData)
    },

    async setDeliveryContacts(userId) {
      const { data } = await api.cart.getDeliveryContacts(userId)
      this.deliveryContacts = data.map(transformDeliveryContact)
    },

    async updateUserData(userId) {
      await this.refreshAccountDetails(userId)
      await Promise.all([
        this.getShippingAddresses(userId),
        this.getBillingAddresses(userId),
        this.setIsReserveOrdersExist(),
      ])
    },

    async updateSomeUserInfoFields(data) {
      await api.user.updateSomeUserInfoFields(data)
      await this.refreshAccountDetails()
    },

    async checkSession() {
      if (!this.isUserLoggedIn) {
        if (!this.isSessionActive) {
          await api.auth.initSession()
        } else {
          return Promise.resolve()
        }
      } else {
        return Promise.resolve()
      }
    },

    resetApiSession() {
      this.lastRequest = null
    },

    async initSessionState() {
      const punchoutStore = usePunchoutStore()
      const asmStore = useAsmStore()
      const cartStore = useCartStore()
      const cartFlowStore = useCartFlowStore()

      if (punchoutStore.isPunchoutSession) {
        if (!punchoutStore.punchoutDunsId) {
          return punchoutStore.punchoutLogout()
        } else {
          await this.updateUserData('current')
          const cartStore = useCartStore()
          await cartStore.getCart(true)
          if (cartStore.productsQty) {
            const cartFlowStore = useCartFlowStore()
            await cartFlowStore.setStep('view')
          }
          return
        }
      }

      if (asmStore.isAsmSession) {
        return asmStore.asmLogout()
      }

      if (this.isUserLoggedIn) await this.updateUserData()
      await cartStore.getCart(true)
      if (cartStore.productsQty) {
        await cartFlowStore.setStep('view')
      }
    },

    async createShortUrl(url) {
      const { data } = await api.user.createShortUrl(url)
      const hash = data.hash
      return hash
    },

    async resolveShortUrl(hash) {
      const { data } = await api.user.resolveShortUrl(hash)
      const url = data.url ? decodeURIComponent(data.url) : ''
      return url
    },

    async setSupportContacts() {
      if (this.isSupportContactsLoaded) return
      const { data } = await api.user.getSupportInfo()
      const supportContacts = []
      const generalSupportContact = {
        name: data.generalSupportName,
        phone: data.generalSupportPhone,
        email: data.generalSupportEmail,
      }
      const isGeneralSupportContactAvailable = Object.values(generalSupportContact).some((el) => el)
      if (isGeneralSupportContactAvailable) supportContacts.push(generalSupportContact)
      this.supportContacts = supportContacts
      this.isSupportContactsLoaded = true
    },

    async resetStateOnLoginChange() {
      const reserveOrdersStore = useReserveOrdersStore()
      const accountPortalStore = useAccountPortalStore()
      const cartStore = useCartStore()
      const productStore = useProductStore()
      const inventoryStore = useInventoryStore()
      const searchStore = useSearchStore()
      const orderTemplateStore = useOrderTemplateStore()
      const inventoryThresholdsStore = useInventoryThresholdsStore()
      const inventoryLogStore = useInventoryLogStore()
      const currentInventoryStore = useCurrentInventoryStore()
      const companyStore = useCompanyStore()
      const rolesStore = useRolesStore()

      await Promise.all([
        reserveOrdersStore.resetState(),
        accountPortalStore.resetAllAccountPortal(),
        cartStore.clearCart(),
        productStore.resetState(),
        inventoryStore.resetState(),
        searchStore.resetState(),
        orderTemplateStore.resetState(),
        inventoryThresholdsStore.resetState(),
        inventoryLogStore.resetState(),
        currentInventoryStore.resetState(),
        companyStore.resetState(),
        rolesStore.resetRoles(),
      ])
    },

    async addRecentlyViewedProduct(slug) {
      await api.user.addRecentProduct(slug)
      this.isRecentProductsLoaded = false
    },

    async getRecentProducts() {
      const response = await api.user.getRecentProducts()
      const { recentlyViewedProducts, recentlyOrderedProducts } = response.data
      const combined = [...recentlyViewedProducts, ...recentlyOrderedProducts]
      if (!combined.length) {
        this.recentlyViewedProducts = []
        this.recentlyOrderedProducts = []
        return
      }

      const queryParams = {
        skip: 0,
        limit: 20,
        include: 4,
        content_type: 'product',
        select:
          'sys.id,fields.slug,fields.title,fields.images,fields.types,fields.subtypes,fields.subtypesLevel2,sys.type',
      } as Record<string, unknown>

      queryParams['fields.slug[in]'] = combined.join(',')

      const { items } =
        await app.config.globalProperties.$contentful.client.getEntries<ContentfulProductSkeleton>(
          queryParams,
        )
      const transformProduct = (slug: string, items: ContentfulProduct[]) => {
        const correspondingEntry = items.find((entry) => entry.fields.slug === slug)
        if (!correspondingEntry || !correspondingEntry.fields.slug) return
        const crumbs = getProductCrumbs(correspondingEntry as ContentfulProductMeta)
        const deepestCrumb = crumbs[crumbs.length - 1]
        return {
          link: `/${correspondingEntry.fields.slug}`,
          title: correspondingEntry.fields.title,
          categoryTitle: deepestCrumb?.name || '',
          categoryLink: deepestCrumb?.link || '',
          image:
            correspondingEntry.fields.images?.[0]?.fields?.file?.url ||
            app.config.globalProperties.$contentful.service.getProductDefaultImage(),
        }
      }
      this.recentlyViewedProducts = recentlyViewedProducts
        .map((el) => transformProduct(el, items))
        .filter((el) => el) as RecentlyViewedProduct[]
      this.recentlyOrderedProducts = recentlyOrderedProducts
        .map((el) => transformProduct(el, items))
        .filter((el) => el) as RecentlyViewedProduct[]
    },

    async getCountriesAndRegions() {
      const countriesResponse = await api.user.getCountries()
      const sortCountries = (countries: { isocode: string; name: string }[]) => {
        const fixedOrder = ['US', 'CA']
        const prioritized = countries.filter((country) => fixedOrder.includes(country.isocode))
        const remaining = countries.filter((country) => !fixedOrder.includes(country.isocode))
        prioritized.sort((a, b) => fixedOrder.indexOf(a.isocode) - fixedOrder.indexOf(b.isocode))
        return [...prioritized, ...remaining]
      }
      const [usRegionsResponse, caRegionsResponse] = await Promise.all([
        api.user.getRegions('US'),
        api.user.getRegions('CA'),
      ])
      this.countries = sortCountries(countriesResponse.data) || []
      this.regions = {
        US: usRegionsResponse.data || [],
        CA: caRegionsResponse.data || [],
      }
    },

    async setIsReserveOrdersExist() {
      const reserveOrdersStore = useReserveOrdersStore()
      await reserveOrdersStore.setIsReserveOrdersExist()
    },

    async setIsRecentProductsLoaded(isRecentProductsLoaded) {
      this.isRecentProductsLoaded = isRecentProductsLoaded
    },
  },
  persist: {
    pick: ['accountDetails', 'lastRequest'],
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSessionStore, import.meta.hot))
}
